import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {InitialAuthService} from "../../shared/auth/initial-auth.service";
import {MatOptionModule} from '@angular/material/core';
import {MatSelectModule} from '@angular/material/select';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatDividerModule} from '@angular/material/divider';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {UtilisateurConnecte} from '../../models/utilisateurConnecte/UtilisateurConnecte';
import {FormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {NgxPermissionsService} from 'ngx-permissions';
import {Router} from '@angular/router';
import {EspaceUtilisateurDialogueComponent} from "../espace-utilisateur-dialogue/espace-utilisateur-dialogue.component";
import {MatBadge} from "@angular/material/badge";
import {NotificationsUsersService} from "../../services/notifications-users/notifications-users.service";
import {NotificationUser} from "../../models/notifications-users/NotificationUserResponse";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {NotificationUserBody} from "../../models/notifications-users/NotificationUserBody";
import {Utilisateur} from "../../models/utilisateur/UtilisateurResponse";
import {interval, Subscription, switchMap} from "rxjs";
import {NotificationsDialogueComponent} from '../notifications-dialogue/notifications-dialogue.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  standalone: true,
  imports: [MatToolbarModule, MatButtonModule, MatIconModule, MatDividerModule, MatFormFieldModule, MatSelectModule, MatOptionModule, MatDialogModule, FormsModule, CommonModule, MatBadge, MatMenu, MatMenuItem, MatMenuTrigger]
})
export class HeaderComponent implements OnChanges {

  @Input() utilisateurConnecte: UtilisateurConnecte | undefined;
  @Input() isUserLoaded: boolean | undefined;
  @Input() permission: string[] | undefined;
  @Input() currentUserLabel: string | undefined;
  @Input() entityWithProfiles: any[] | undefined;
  @Input() principalEntityId: number | undefined;
  @Input() currentUser!: Utilisateur | null;
  @Output() profileChanged = new EventEmitter<string>();

  permissions: string[] = []
  selectedEntity: string | undefined;
  profiles: string[] = [];
  selectedProfile: string | undefined;
  notifications: NotificationUser[] = [];
  notificationsCount: number = 0;
  clickedNotificationId: number | null = null;
  private notificationSubscription: Subscription | undefined;

  constructor(private auth: InitialAuthService, public dialog: MatDialog, private permissionsService: NgxPermissionsService,
              private router: Router, private notificationsService: NotificationsUsersService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityWithProfiles'] || changes['principalEntityId']) {
      this.initializeSelection();
      this.permissionsService.loadPermissions(this.permissions);
      this.getNotifications();
      this.startNotificationPolling();
    }
  }

  getNotifications(): void {
    this.notificationsService.getNotifications({user_id: this.currentUser?.id!}).subscribe(response => {
      this.notifications = response.data;
      this.notificationsCount = this.notifications.filter(notification => !notification.is_read).length;
    });
  }

  startNotificationPolling(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
    this.notificationSubscription = interval(30000)
      .pipe(
        switchMap(() => this.notificationsService.getNotifications({user_id: this.currentUser?.id!}))
      )
      .subscribe(response => {
        this.notifications = response.data;
        this.notificationsCount = this.notifications.filter(notification => !notification.is_read).length;
      });
  }


  initializeSelection(): void {
    if (this.entityWithProfiles && this.principalEntityId) {
      this.entityWithProfiles.sort((a, b) => a.entity.localeCompare(b.entity));
      const principalEntity = this.entityWithProfiles.find(e => e.entityId === this.principalEntityId);
      if (principalEntity) {
        this.selectedEntity = principalEntity.entity;
        this.profiles = principalEntity.profiles;
        this.profiles.sort((a, b) => a.localeCompare(b));
        this.selectedProfile = this.profiles.length > 0 ? this.profiles[0] : undefined;
        this.profileChanged.emit(this.selectedProfile);
        if (this.selectedProfile) this.permissions.push(this.selectedProfile);
      } else if (this.entityWithProfiles.length > 0) {
        this.selectedEntity = this.entityWithProfiles[0].entity;
        this.profiles = this.entityWithProfiles[0].profiles.filter((profile: string) => profile !== 'PCL');
        this.profiles.sort((a, b) => a.localeCompare(b));
        this.selectedProfile = this.profiles.length > 0 ? this.profiles[0] : undefined;
        this.profileChanged.emit(this.selectedProfile);
        if (this.selectedProfile) this.permissions.push(this.selectedProfile);
      }
    }
  }

  updatePermissions(): void {
    this.permissions = [];
    if (this.selectedProfile) {
      this.permissions.push(this.selectedProfile);
      this.permissionsService.loadPermissions(this.permissions);
    }
  }

  onEntitySelect(selectedEntity: string): void {
    const entity = this.entityWithProfiles?.find(e => e.entity === selectedEntity);
    if (entity) {
      this.profiles = entity.profiles;
      this.profiles.sort((a, b) => a.localeCompare(b));
      this.selectedProfile = this.profiles.length > 0 ? this.profiles[0] : undefined;
      this.profileChanged.emit(this.selectedProfile);
      this.updatePermissions();
      this.navigateToDashboard();
    }
  }

  onProfileSelect(selectedProfile: string): void {
    this.selectedProfile = selectedProfile;
    this.profileChanged.emit(this.selectedProfile);
    this.updatePermissions();
    this.navigateToDashboard();
  }

  getInitials(): string {
    const fullName = this.currentUserLabel ? this.currentUserLabel : '';
    const names = fullName.split(' ');
    return `${names[0].charAt(0)}${names[1].charAt(0)}`;
  }

  logout() {
    this.auth.logoutSession();
  }

  openEditDialog(): void {
    const dialogRef = this.dialog.open(EspaceUtilisateurDialogueComponent, {
      width: '1200px',
      height: '450px',
      position: {top: '65px', right: '0px'},
      data: {utilisateurConnecteMail: this.utilisateurConnecte?.mail}
    });
  }

  navigateToDashboard() {
    this.router.navigate(['liste-procedures']);
  }

  onNotificationClick(notification: NotificationUser): void {
    if (!notification.is_read) {
      const updateData: NotificationUserBody = {
        user_id: notification.user_id,
        notification_id: notification.notification_id,
        is_read: true,
        read_at: new Date().toISOString()
      };
      this.updateNotificationStatus(notification.id, updateData);
    }

    if (notification.notification?.procedure_id) {
      this.router.navigate(['procedures/modifier-procedure', notification.notification.procedure_id]);
    }
  }


  updateNotificationStatus(notificationId: number, updateData: NotificationUserBody): void {
    this.notificationsService.updateNotification(notificationId, updateData).subscribe(updatedNotification => {
      const index = this.notifications.findIndex(n => n.id === notificationId);
      if (index !== -1) {
        this.notifications[index] = { ...this.notifications[index], ...updatedNotification.data };
        this.notificationsCount = this.notifications.filter(notification => !notification.is_read).length;
      }
    });
  }

  openNotificationsDialog(): void {
    const dialogRef = this.dialog.open(NotificationsDialogueComponent, {
      width: '700px',
      position: { top: '50px', right: '0px' },
      data: { userId: this.currentUser?.id }
    });
  }
}
